import React from "react";

// Sample B — "Aurora"
// Modern, premium, slightly futuristic. Deep ink-blue base, glassmorphic cards,
// aurora gradient accents (violet → cyan → mint), tight grotesk type.

const SampleB = () => {
  return (
    <div
      data-testid="sample-b-root"
      className="min-h-screen relative overflow-hidden"
      style={{
        background: "#0A0F1F",
        color: "#E7ECF7",
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');
        .sb-grotesk { font-family: 'Space Grotesk', sans-serif; letter-spacing: -0.02em; }
        .sb-glass {
          background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02));
          border: 1px solid rgba(255,255,255,0.08);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          border-radius: 22px;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 24px 60px -30px rgba(0,0,0,0.7);
          transition: transform .35s cubic-bezier(.2,.7,.2,1), border-color .35s, box-shadow .35s;
        }
        .sb-glass:hover { transform: translateY(-3px); border-color: rgba(173,140,255,0.35); }
        .sb-aurora-text {
          background: linear-gradient(90deg, #B5A0FF 0%, #6CD3F2 50%, #7CF2C5 100%);
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        .sb-pill {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 9px 16px; border-radius: 999px;
          background: linear-gradient(90deg, #B5A0FF, #6CD3F2);
          color: #0A0F1F; font-weight: 600; font-size: 13px;
          box-shadow: 0 8px 24px -8px rgba(108,211,242,0.55);
          transition: transform .25s, filter .25s;
        }
        .sb-pill:hover { transform: translateY(-1px); filter: brightness(1.08); }
        .sb-chip {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 12px; border-radius: 999px;
          font-size: 11px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase;
          background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.08);
          color: #C8D1E5;
        }
        .sb-aurora-bg::before {
          content: ''; position: absolute; inset: -20%; z-index: 0;
          background:
            radial-gradient(60% 40% at 20% 0%, rgba(181,160,255,0.35), transparent 60%),
            radial-gradient(40% 30% at 80% 10%, rgba(108,211,242,0.30), transparent 60%),
            radial-gradient(50% 35% at 60% 100%, rgba(124,242,197,0.25), transparent 60%);
          filter: blur(40px);
          pointer-events: none;
        }
        .sb-noise {
          position: absolute; inset: 0; pointer-events: none; opacity: .04; mix-blend-mode: overlay;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
        }
        @keyframes sbPulse { 0%,100% { opacity: .8 } 50% { opacity: 1 } }
        .sb-pulse { animation: sbPulse 2.4s ease-in-out infinite; }
        .sb-grid-bg {
          background-image:
            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
          background-size: 56px 56px;
        }
      `}</style>

      <div className="sb-aurora-bg absolute inset-0" />
      <div className="sb-noise" />
      <div className="absolute inset-0 sb-grid-bg opacity-50" />

      {/* Top bar */}
      <header className="relative z-10 px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center sb-grotesk text-lg font-bold"
            style={{ background: "linear-gradient(135deg,#B5A0FF,#6CD3F2)", color: "#0A0F1F" }}
          >
            f
          </div>
          <div>
            <div className="sb-grotesk text-xl font-bold leading-none">FamilyOS</div>
            <div className="text-xs opacity-60 mt-1">The Anderson Family</div>
          </div>
        </div>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium opacity-80">
          <span>Home</span><span>Calendar</span><span>Recipes</span><span>Journal</span><span>Tracker</span>
        </nav>
        <div className="flex items-center gap-3">
          <button className="sb-pill">+ Quick add</button>
          <div className="w-9 h-9 rounded-full ring-2"
               style={{ background: "linear-gradient(135deg,#7CF2C5,#6CD3F2)", boxShadow: "0 0 0 2px rgba(255,255,255,0.06)" }} />
        </div>
      </header>

      <main className="relative z-10 px-10 pb-20 max-w-7xl mx-auto">
        {/* Hero */}
        <section className="grid md:grid-cols-3 gap-8 items-end mb-10">
          <div className="md:col-span-2">
            <div className="sb-chip mb-5">
              <span className="sb-pulse" style={{ width: 6, height: 6, borderRadius: 999, background: "#7CF2C5" }} />
              Sun · 09:14 · light rain
            </div>
            <h1 className="sb-grotesk text-6xl md:text-7xl font-bold leading-[0.95]">
              Good morning,<br />
              <span className="sb-aurora-text">Maya</span>.
            </h1>
            <p className="mt-5 text-sm opacity-70 max-w-lg leading-relaxed">
              3 things deserve attention today. Toby leaves for soccer in 2h.
              The household pulse looks calm.
            </p>
            <div className="mt-7 flex gap-3">
              <button className="sb-pill">Open today</button>
              <button className="sb-chip" style={{ padding: "9px 16px", textTransform: "none", letterSpacing: 0 }}>
                Ask AI →
              </button>
            </div>
          </div>

          <div className="sb-glass p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs uppercase tracking-widest opacity-60">Pulse</span>
              <span className="sb-chip">live</span>
            </div>
            <div className="space-y-3">
              {[
                ["Calendar load", 42, "#B5A0FF"],
                ["Tasks done", 78, "#7CF2C5"],
                ["Budget used", 31, "#6CD3F2"],
              ].map(([label, val, c], i) => (
                <div key={i}>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="opacity-80">{label}</span>
                    <span className="sb-grotesk font-semibold">{val}%</span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
                    <div style={{ width: `${val}%`, background: c, height: "100%", borderRadius: 999 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tile grid */}
        <section className="grid md:grid-cols-3 gap-6 mb-10">
          <div className="sb-glass p-7">
            <div className="flex items-center justify-between mb-5">
              <span className="sb-grotesk text-lg font-semibold">Today</span>
              <span className="sb-chip">4 events</span>
            </div>
            <ul className="space-y-4">
              {[
                ["08:30", "School drop-off", "#B5A0FF"],
                ["10:00", "Soccer · Toby", "#7CF2C5"],
                ["14:00", "Dentist · Mom", "#6CD3F2"],
                ["18:00", "Family dinner", "#FFB37C"],
              ].map(([t, label, c], i) => (
                <li key={i} className="flex items-center gap-4">
                  <span className="sb-grotesk text-xs opacity-60 w-12">{t}</span>
                  <span style={{ background: c, boxShadow: `0 0 14px ${c}` }} className="w-2 h-2 rounded-full" />
                  <span className="text-sm font-medium">{label}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="sb-glass p-7 relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full"
                 style={{ background: "radial-gradient(circle, rgba(181,160,255,0.5), transparent 70%)" }} />
            <div className="relative">
              <span className="sb-chip mb-5">Tonight</span>
              <div className="sb-grotesk text-2xl font-bold leading-tight mb-2">
                Slow-roasted herb chicken
              </div>
              <p className="text-xs opacity-70 mb-5">40 min · 6 servings · 720 kcal</p>
              <div className="flex flex-wrap gap-2">
                {["Rosemary", "Lemon", "Garlic"].map((t) => (
                  <span key={t} className="sb-chip" style={{ textTransform: "none", letterSpacing: 0 }}>{t}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="sb-glass p-7">
            <div className="flex items-center justify-between mb-5">
              <span className="sb-grotesk text-lg font-semibold">Family circle</span>
              <span className="sb-pulse w-2 h-2 rounded-full" style={{ background: "#7CF2C5" }} />
            </div>
            <div className="space-y-4">
              {[
                ["Maya", "Home", "#B5A0FF"],
                ["David", "Coffee · 0.4 mi", "#6CD3F2"],
                ["Toby", "School", "#7CF2C5"],
                ["Lily", "Grandma's", "#FFB37C"],
              ].map(([name, where, c], i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs sb-grotesk font-bold"
                       style={{ background: c, color: "#0A0F1F" }}>{name[0]}</div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{name}</div>
                    <div className="text-xs opacity-50">{where}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Wide tiles */}
        <section className="grid md:grid-cols-2 gap-6">
          <div className="sb-glass p-7 relative overflow-hidden">
            <div className="absolute inset-0"
                 style={{ background: "linear-gradient(135deg, rgba(181,160,255,0.18), transparent 50%)" }} />
            <div className="relative">
              <span className="sb-chip mb-5">AI · weekly briefing</span>
              <div className="sb-grotesk text-2xl font-bold leading-tight mb-3">
                Calmer week ahead. Thursday evening is wide open.
              </div>
              <p className="text-sm opacity-70 mb-5 leading-relaxed">
                No overlaps detected. Toby's tutor + Lily's piano stack on Tuesday.
                Date night opportunity flagged.
              </p>
              <button className="sb-pill">Read briefing →</button>
            </div>
          </div>

          <div className="sb-glass p-7">
            <div className="flex items-center justify-between mb-4">
              <span className="sb-grotesk text-lg font-semibold">Recent journal</span>
              <span className="sb-chip">3 new</span>
            </div>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-14 h-14 rounded-xl"
                     style={{ background: "linear-gradient(135deg,#B5A0FF,#6CD3F2)" }} />
                <div>
                  <div className="text-sm font-semibold">Lily's first lost tooth</div>
                  <div className="text-xs opacity-50">Maya · 2h ago</div>
                  <p className="text-sm mt-1 opacity-80">Tiny note under the pillow.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-14 h-14 rounded-xl"
                     style={{ background: "linear-gradient(135deg,#7CF2C5,#6CD3F2)" }} />
                <div>
                  <div className="text-sm font-semibold">First garden sprouts</div>
                  <div className="text-xs opacity-50">David · 1d ago</div>
                  <p className="text-sm mt-1 opacity-80">Tomatoes coming in. Basil still asleep.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="relative z-10 px-10 pb-10 text-center text-xs opacity-40">
        Sample B · Aurora — modern, premium, soft-futuristic
      </footer>
    </div>
  );
};

export default SampleB;
